import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaBriefcase, FaBuilding, FaCheckCircle, FaTimesCircle, FaEye, FaUser, FaMapMarkerAlt, FaMoneyBillWave, FaClock, FaTimes, FaCheck, FaExclamationTriangle } from 'react-icons/fa';
import DataTable from '../shared/DataTable';
import Loader from '../../shared/Loader';
import { showSuccess, showError, showConfirm, showLoading, closeLoading } from '../../../utils/Alert';
import './JobModeration.css';

const JobModeration = () => {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('pending');

    const [selectedJob, setSelectedJob] = useState(null);
    const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);

    useEffect(() => {
        fetchJobs();
    }, [activeTab]);

    const fetchJobs = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const statusMap = { pending: 'Pending', active: 'Active', rejected: 'Rejected' };
            const res = await axios.get(`/api/admin/jobs?status=${statusMap[activeTab]}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setJobs(res.data);
        } catch (err) {
            showError('Fetch Failed', 'Could not load job postings for moderation.');
        } finally {
            setLoading(false);
        }
    };

    const doStatusUpdate = async (id, status, feedback = '') => {
        showLoading(status === 'Active' ? 'Approving...' : 'Rejecting...');
        try {
            const token = localStorage.getItem('token');
            await axios.patch(`/api/admin/jobs/${id}/status`, { status, adminFeedback: feedback }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            closeLoading();
            showSuccess(
                status === 'Active' ? 'Job Approved' : 'Job Rejected',
                status === 'Active'
                    ? 'The listing is now live and visible to candidates.'
                    : 'The recruiter has been notified with your feedback.'
            );
            setIsReviewModalOpen(false);
            fetchJobs();
        } catch (err) {
            closeLoading();
            showError('Action Failed', 'Could not update the job status. Please try again.');
        }
    };

    const handleApprove = async (id) => {
        const result = await showConfirm(
            'Approve this Posting?',
            'This job will immediately go live and be visible to all candidates on the platform.'
        );
        if (result.isConfirmed) {
            await doStatusUpdate(id, 'Active');
        }
    };

    // SweetAlert with HTML input for rejection note
    const handleReject = async (id) => {
        const Swal = (await import('sweetalert2')).default;
        const result = await Swal.fire({
            title: 'Rejection Note',
            html: `
                <p style="color:#64748b;font-size:14px;margin-bottom:16px">
                    Please provide a reason for rejection. This note will be sent directly to the recruiter.
                </p>
                <textarea id="reject-note" rows="4" placeholder="e.g. Job description lacks sufficient detail. Please provide role expectations, responsibilities, and required qualifications."
                    style="width:100%;padding:12px;border:1.5px solid #e2e8f0;border-radius:10px;
                    font-family:inherit;font-size:14px;resize:vertical;box-sizing:border-box;"
                ></textarea>`,
            showCancelButton: true,
            confirmButtonText: 'Reject & Notify Recruiter',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#94a3b8',
            focusConfirm: false,
            preConfirm: () => {
                const note = document.getElementById('reject-note').value.trim();
                if (!note) {
                    Swal.showValidationMessage('A rejection note is required to notify the recruiter.');
                    return false;
                }
                return note;
            }
        });
        if (result.isConfirmed && result.value) {
            await doStatusUpdate(id, 'Rejected', result.value);
        }
    };

    const columns = [
        {
            header: 'Job Information',
            key: 'title',
            width: '35%',
            render: (row) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div className="job-icon-box"><FaBriefcase /></div>
                    <div>
                        <div style={{ fontWeight: 600, color: 'var(--primary-blue)' }}>{row.title}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <FaBuilding fontSize="10px" /> {row.company}
                        </div>
                    </div>
                </div>
            )
        },
        {
            header: 'Recruiter',
            key: 'recruiterId',
            width: '15%',
            render: (row) => row.recruiterId?.name || 'N/A'
        },
        { header: 'Location', key: 'location', width: '15%' },
        {
            header: 'Salary Range',
            key: 'salaryRange',
            width: '15%',
            render: (row) => row.salaryRange || row.salary || 'Not specified'
        },
        {
            header: 'Actions',
            key: 'actions',
            width: '20%',
            render: (row) => (
                <div className="actions-cell">
                    <button
                        className="icon-btn view-btn"
                        title="Review Details"
                        onClick={() => { setSelectedJob(row); setIsReviewModalOpen(true); }}
                    >
                        <FaEye />
                    </button>
                    {row.status === 'Pending' && (
                        <>
                            <button className="icon-btn approve-btn" onClick={() => handleApprove(row._id)} title="Approve"><FaCheckCircle /></button>
                            <button className="icon-btn reject-btn" onClick={() => handleReject(row._id)} title="Reject"><FaTimesCircle /></button>
                        </>
                    )}
                </div>
            )
        }
    ];

    const displayedJobs = jobs.filter(j => j.status === activeTab);

    if (loading) {
        return <Loader />;
    }

    return (
        <div className="job-moderation fade-in">
            <div className="dashboard-header">
                <h1 className="page-title">Career Opportunities</h1>
                <p className="page-subtitle">Vetting and optimizing professional connectors between talent and industry.</p>
            </div>

            <div className="management-tabs">
                <button
                    className={`tab-item ${activeTab === 'pending' ? 'selected' : ''}`}
                    onClick={() => setActiveTab('pending')}
                >
                    <FaClock /> Pending Review
                </button>
                <button
                    className={`tab-item ${activeTab === 'active' ? 'selected' : ''}`}
                    onClick={() => setActiveTab('active')}
                >
                    <FaCheckCircle /> Active Jobs
                </button>
                <button
                    className={`tab-item ${activeTab === 'rejected' ? 'selected' : ''}`}
                    onClick={() => setActiveTab('rejected')}
                >
                    <FaTimesCircle /> Archived/Rejected
                </button>
            </div>

            <DataTable
                title={`${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Postings`}
                columns={columns}
                data={jobs}
            />

            {/* Job Review Modal */}
            {isReviewModalOpen && selectedJob && (
                <div className="modal-overlay open" onClick={() => setIsReviewModalOpen(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2><FaBriefcase /> Review Job Posting</h2>
                            <button className="modal-close-btn" onClick={() => setIsReviewModalOpen(false)}><FaTimes /></button>
                        </div>

                        <div className="modal-body">
                            <div className="job-detailed-view">
                                {/* Job Meta */}
                                <div className="job-main-info">
                                    <div style={{ marginBottom: 20 }}>
                                        <h3 style={{ margin: '0 0 10px', fontSize: 20, color: '#1e3a8a' }}>{selectedJob.title}</h3>
                                        <span className={`status-badge ${selectedJob.status.toLowerCase()}`}>{selectedJob.status}</span>
                                    </div>
                                    <div className="meta-grid">
                                        <div className="meta-item"><FaBuilding /> <strong>Company:</strong> {selectedJob.company}</div>
                                        <div className="meta-item"><FaUser /> <strong>Recruiter:</strong> {selectedJob.recruiterId?.name || 'Unknown'}</div>
                                        <div className="meta-item"><FaMapMarkerAlt /> <strong>Location:</strong> {selectedJob.location}</div>
                                        <div className="meta-item"><FaMoneyBillWave /> <strong>Compensation:</strong> {selectedJob.salaryRange || selectedJob.salary || 'Not specified'}</div>
                                    </div>
                                </div>

                                {/* Description & Requirements */}
                                <div className="job-content-review">
                                    <h4>Job Description</h4>
                                    <p className="description-text">{selectedJob.description}</p>

                                    {Array.isArray(selectedJob.requirements) && selectedJob.requirements.length > 0 && (
                                        <>
                                            <h4>Requirements</h4>
                                            <ul className="requirements-list">
                                                {selectedJob.requirements.filter(r => r).map((req, i) => <li key={i}>{req}</li>)}
                                            </ul>
                                        </>
                                    )}
                                </div>

                                {/* Action Buttons — No inline textarea, rejection note via SweetAlert */}
                                {selectedJob.status === 'Pending' && (
                                    <div className="resolution-actions">
                                        <button
                                            className="action-btn-secondary"
                                            onClick={() => handleReject(selectedJob._id)}
                                        >
                                            <FaTimes /> Reject Posting
                                        </button>
                                        <button
                                            className="action-btn-gold"
                                            onClick={() => handleApprove(selectedJob._id)}
                                        >
                                            <FaCheck /> Approve & Go Live
                                        </button>
                                    </div>
                                )}
                                {selectedJob.status !== 'Pending' && (
                                    <div className="status-note">
                                        This posting has already been <strong>{selectedJob.status}</strong>.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div >
    );
};

export default JobModeration;
